"use client";

import { c as _c } from "react/compiler-runtime";
import { getScaledResolvedImageUri, ImageScale } from "@/helpers/image.helpers";
import { TOOLTIP_STYLES } from "@/helpers/tooltip.helpers";
import useIsTouchDevice from "@/hooks/useIsTouchDevice";
import Image from "next/image";
import Link from "next/link";
import type { MouseEvent, ReactNode } from "react";
import { useId } from "react";
import { Tooltip } from "react-tooltip";
import { useGatewayImageLoadState } from "@/components/common/image/useGatewayImageLoadState";
interface OverlappingAvatarItem {
  readonly key: string;
  readonly pfpUrl: string | null;
  readonly href?: string;
  readonly ariaLabel?: string;
  readonly fallback?: string;
  readonly title?: string;
  readonly tooltipContent?: ReactNode;
}
interface OverlappingAvatarsProps {
  readonly items: OverlappingAvatarItem[];
  readonly maxCount?: number;
  readonly size?: "sm" | "md";
  readonly overlapClass?: string;
  readonly onItemClick?: (e: MouseEvent<HTMLAnchorElement>, item: OverlappingAvatarItem) => void;
}
const SIZE_CLASS = {
  sm: "tw-h-6 tw-w-6",
  md: "tw-h-7 tw-w-7"
} as const;
function AvatarContent(t0) {
  const $ = _c(11);
  const {
    pfpUrl,
    ariaLabel,
    fallback,
    avatarRing
  } = t0;
  const {
    activeSrc,
    isPlaceholder,
    unoptimized,
    handleError
  } = useGatewayImageLoadState(pfpUrl);
  if (isPlaceholder || activeSrc === null) {
    const t1 = fallback ?? "?";
    let t2;
    if ($[0] !== t1) {
      t2 = <div className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-rounded-full tw-bg-iron-700 tw-ring-[1.5px] tw-ring-black"><span className="tw-text-[0.625rem] tw-font-semibold tw-text-iron-300">{t1}</span></div>;
      $[0] = t1;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    return t2;
  }
  const t1 = `${activeSrc}-${unoptimized ? "unoptimized" : "optimized"}`;
  let t2;
  if ($[2] !== activeSrc) {
    t2 = getScaledResolvedImageUri(activeSrc, ImageScale.W_AUTO_H_50);
    $[2] = activeSrc;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = ariaLabel ?? "Profile";
  const t4 = `tw-rounded-full tw-object-cover ${avatarRing}`;
  let t5;
  if ($[4] !== handleError || $[5] !== t1 || $[6] !== t2 || $[7] !== t3 || $[8] !== t4 || $[9] !== unoptimized) {
    t5 = <Image key={t1} src={t2} alt={t3} fill={true} sizes="28px" unoptimized={unoptimized} onError={handleError} className={t4} />;
    $[4] = handleError;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = unoptimized;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}
export default function OverlappingAvatars(t0) {
  const $ = _c(12);
  const {
    items,
    maxCount: t1,
    size: t2,
    overlapClass: t3,
    onItemClick
  } = t0;
  const maxCount = t1 === undefined ? 5 : t1;
  const size = t2 === undefined ? "md" : t2;
  const overlapClass = t3 === undefined ? "-tw-space-x-2" : t3;
  const baseId = useId();
  const isTouchDevice = useIsTouchDevice();
  let t4;
  let t5;
  if ($[0] !== baseId || $[1] !== isTouchDevice || $[2] !== items || $[3] !== maxCount || $[4] !== onItemClick || $[5] !== overlapClass || $[6] !== size) {
    const slice = items.slice(0, maxCount);
    const sizeClass = SIZE_CLASS[size];
    t4 = `tw-flex tw-items-center tw-overflow-visible ${overlapClass}`;
    t5 = slice.map((item, index) => {
      let transformOrigin;
      if (index === 0) {
        transformOrigin = "left center";
      } else {
        if (index === slice.length - 1) {
          transformOrigin = "right center";
        } else {
          transformOrigin = "center center";
        }
      }
      const content = <AvatarContent pfpUrl={item.pfpUrl} ariaLabel={item.ariaLabel} fallback={item.fallback} avatarRing="tw-rounded-full tw-bg-iron-700 tw-ring-[1.5px] tw-ring-black tw-object-cover" />;
      const showTooltip = !isTouchDevice && item.title !== undefined && item.title !== "";
      const tooltipId = showTooltip ? `${baseId}-${index}` : undefined;
      const wrapper = <div key={item.key} className={`tw-relative tw-flex-shrink-0 tw-overflow-visible ${sizeClass} ${"tw-transition-transform tw-duration-200 tw-ease-out hover:tw-scale-110 hover:!tw-z-[100]"}`} style={{
        zIndex: slice.length - index,
        transformOrigin
      }} {...tooltipId !== undefined && {
        "data-tooltip-id": tooltipId
      }}>{content}</div>;
      const anchor = item.href ? <Link key={item.key} href={item.href} className="tw-m-0 tw-inline-flex tw-border-none tw-bg-transparent tw-p-0" aria-label={item.ariaLabel} onClick={e => onItemClick?.(e, item)}>{wrapper}</Link> : wrapper;
      if (showTooltip && tooltipId !== undefined) {
        return <span key={item.key} className="tw-inline-flex">{anchor}<Tooltip id={tooltipId} place="top" delayShow={250} style={TOOLTIP_STYLES}>{item.tooltipContent ?? item.title}</Tooltip></span>;
      }
      return anchor;
    });
    $[0] = baseId;
    $[1] = isTouchDevice;
    $[2] = items;
    $[3] = maxCount;
    $[4] = onItemClick;
    $[5] = overlapClass;
    $[6] = size;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t4 || $[10] !== t5) {
    t6 = <div className={t4}>{t5}</div>;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
}